import { EAxiosResponseCode, getHttpErrorMessage, Http } from '@core/api';
import { getHostbaseUrl } from '@core/tools';
import { 
  IFreelancerCreate, 
  IFreelancerDetail, 
  IFreelancerGroupItem, 
  IFreelancerItem, 
  IFreelancerListParams, 
  IImportFreelancer, 
  IImportFreelancerParams, 
  IFreelancerDistributionRecordParams, 
  IFreelancerDistributionRecordItem, 
  ISendSmsFreelancerParams, 
  IFreelancerSmsTemplateResp, 
  IImportDutyFreelancerParams, 
  IFreelancerServerItme,
  IFreelancerBankAddParams,
  IFreelancerAddPayAccountParams,
  IFreelancerDutyGroupItem,
  IUpdateHnAuthDateParam,
} from '../../types/app-account/freelancer';
import { IDutySignUpUser, IDutySignUpUserNonAudit, IDutySignUpUserParams } from '../../types/crowd-source/task';

const hostUrl = getHostbaseUrl();

/**
 * 账户中心 - 自由职业者列表（分页）
 *  @param params IFreelancerListParams
 */
export async function queryFreelancerPage(params: IFreelancerListParams) {
  let data = [] as IFreelancerItem[];
  let total = 0;

  params = JSON.parse(JSON.stringify(params)) as IFreelancerListParams;

  try {
    const { code, context, message } = await Http.get<{ data: IFreelancerItem[]; total: number }>(
      `${hostUrl}/iam/customer/page`, 
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IFreelancerItem[]; total: number };

      data = items || [];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 账户中心 - 导入自由职业者
 * @param params IImportFreelancerParams
 */
export async function importFreelancer(params: IImportFreelancerParams) {
  try {
    const { code, context, message } = await Http.post<IImportFreelancer>(
      `${hostUrl}/iam/customer/import`, 
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {} as IImportFreelancer;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 新增自由职业者
 * @param params IFreelancerCreate
 */
export async function addFreelancer(params: IFreelancerCreate) {
  try {
    const { code, context, message } = await Http.post<any>(
      `${hostUrl}/iam/customer/add`, 
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 编辑自由职业者
 */
export async function editFreelancer(params: { groupId: number; idNo: string; phoneList: string[] }) {
  try {
    const { code, context, message } = await Http.post<any>(
      `${hostUrl}/iam/customer/edit`, 
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 导出自由职业者
 * @param params IFreelancerListParams
 */
export async function exportFreelancerPage(params: IFreelancerListParams) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/iam/customer/export`,
      params,
      { responseType: 'blob' },
    );

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 账户中心 - 自由职业者详情
 * @param idNo 身份证号
 */
export async function queryFreelancerDetail(idNo: string) {
  const res = {} as IFreelancerDetail;

  try {
    const { code, context, message } = await Http.get<IFreelancerDetail>(
      `${hostUrl}/iam/customer/detail`,
      { idNo },
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || res;
    }
    throw new Error(message || '服务器异常，请稍后再试~');
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 新增银行卡
 */
export async function addFreelancerBank(params: IFreelancerBankAddParams) {
  try {
    const { code, context, message } = await Http.post<any>(
      `${hostUrl}/iam/customer/add-bank-card`, 
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 新增收款账户
 */
export async function addPayAccount(params: IFreelancerAddPayAccountParams) {
  try {
    const { code, context, message } = await Http.post<any>(
      `${hostUrl}/iam/customer/add-pay-account`, 
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 分组列表
 */
export async function queryFreelancerGroup() {
  let res = [] as IFreelancerGroupItem[];

  try {
    const { code, context, message } = await Http.get<IFreelancerGroupItem[]>(
      `${hostUrl}/iam/customer/list-group`, 
      {},
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || [] as IFreelancerGroupItem[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 账户中心 - 新增分组
 */
export async function addFreelancerGroup(params: { groupName: string }) {
  try {
    const { code, context, message } = await Http.post<any>(
      `${hostUrl}/iam/customer/add-group`, 
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 编辑分组
 */
export async function editFreelancerGroup(params: { groupId: number; groupName: string }) {
  try {
    const { code, context, message } = await Http.post<any>(
      `${hostUrl}/iam/customer/edit-group`, 
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 删除分组
 */
export async function deleteFreelancerGroup(params: { groupId: number }) {
  try {
    const { code, context, message } = await Http.post<any>(
      `${hostUrl}/iam/customer/delete-group`, 
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 编辑分组
 */
export async function transferFreelancerGroup(params: { groupId: number; idNos: string[] }) {
  try {
    const { code, context, message } = await Http.post<any>(
      `${hostUrl}/iam/customer/transfer-group`, 
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 发送短信
 */
export async function sendSmsFreelancer(params: ISendSmsFreelancerParams) {
  try {
    const { code, context, message } = await Http.post<any>(
      `${hostUrl}/iam/customer/send-sms`, 
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 签名发送短信
 */
export async function signSendSmsFreelancer(params: ISendSmsFreelancerParams) {
  try {
    const { code, context, message } = await Http.post<any>(
      `${hostUrl}/iam/customer/sign-send-sms`, 
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 发放记录
 */
export async function queryFreelancerDistributionRecord(params: IFreelancerDistributionRecordParams) {
  let data = [] as IFreelancerDistributionRecordItem[];
  let total = 0;

  params = JSON.parse(JSON.stringify(params)) as IFreelancerDistributionRecordParams;

  try {
    const { code, context, message } = await Http.post<{ data: IFreelancerDistributionRecordItem[]; total: number }>(
      `${hostUrl}/pay/agg/order/pc-page`, 
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IFreelancerDistributionRecordItem[]; total: number };

      data = items || [];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 账户中心 - 自由职业者 - 短信模板列表
 */
export async function querySmsTemplate(idNo?: string) {
  let res = [] as IFreelancerSmsTemplateResp[];

  try {
    const { code, context, message } = await Http.get<IFreelancerSmsTemplateResp[]>(
      `${hostUrl}/iam/customer/sms-template/list`, 
      { idNo },
    );

    if (code === EAxiosResponseCode.Succeed) {
      res = context || [] as IFreelancerSmsTemplateResp[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return res;
}

/**
 * 账户中心 - 任务导入自由职业者
 * @param params IImportDutyFreelancerParams
 */
export async function importDutyFreelancer(params: IImportDutyFreelancerParams) {
  try {
    const { code, context, message } = await Http.post<IImportFreelancer>(
      `${hostUrl}/iam/customer/duty-import`, 
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {} as IImportFreelancer;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 自由职业者列表（任务派发用）
 */
export async function queryFreelancerDutyList() {
  try {
    const { code, context, message } = await Http.get<IFreelancerDutyGroupItem[]>(
      `${hostUrl}/iam/customer/duty-list`, 
      {},
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || [] as IFreelancerDutyGroupItem[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 账户中心 - 自由职业者获取服务商列表
 */
export async function queryFreelancerServerList() {
  try {
    const { code, context, message } = await Http.get<IFreelancerServerItme[]>(
      `${hostUrl}/iam/customer/server-list`, 
      {},
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || [] as IFreelancerServerItme[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 报名人员信息分页
 *  @param params IDutySignUpUserParams
 */
export async function queryFreelanceSignUpPage(params: IDutySignUpUserParams) {
  let data = [] as IDutySignUpUser[];
  let total = 0;

  params = JSON.parse(JSON.stringify(params)) as IDutySignUpUserParams;

  try {
    const { code, context, message } = await Http.post<{ data: IDutySignUpUser[]; total: number }>(
      `${hostUrl}/iam/customer/sign-up-user/page`, 
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IDutySignUpUser[]; total: number };

      data = items || [];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 待审批报名人员
 *  @param params IDutySignUpUserParams
 */
export async function queryFreelanceSignUpNonAuditPage(params: IDutySignUpUserParams) {
  let data = [] as IDutySignUpUserNonAudit[];
  let total = 0;

  params = JSON.parse(JSON.stringify(params)) as IDutySignUpUserParams;

  try {
    const { code, context, message } = await Http.get<{ data: IDutySignUpUserNonAudit[]; total: number }>(
      `${hostUrl}/side/duty/sign-up-user/non-audit-page`, 
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IDutySignUpUserNonAudit[]; total: number };

      data = items || [];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 待审批报名人员人数
 *  @param params IDutySignUpUserParams
 */
export async function queryFreelanceSignUpNonAuditCount(id: number) {

  try {
    const { code, context, message } = await Http.get<{ count: number }>(
      `${hostUrl}/side/duty/sign-up-user/non-audit-count`, 
      { id },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const { count } = context || {} as { count: number };

      return count; 
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

}

/**
 * 更新自由职业者海南授权时间
 * @param params IUpdateHnAuthDateParam
 */
export async function updateFreelancerHnAuthDate(params: IUpdateHnAuthDateParam) {
  try {
    const { code, context, message } = await Http.post<any>(
      `${hostUrl}/iam/customer/update-hn-auth-date`, 
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}